<template>
	<div class="mt-8">
		<div class="flex items-baseline gap-x-1">
			<h2
				v-if="hasMarker"
				class="m-0 font-heading text-2xxl font-bold leading-none text-gray-600 line-through"
			>
				{{ formatCurrency(marker) }}
			</h2>
			<h2 class="m-0 font-heading text-2xxl font-bold leading-none">
				{{ pricing > 0 ? formatCurrency(pricing) : '' }}
			</h2>
			<span class="text-base">per week</span>
		</div>
		<div class="mt-2">
			<!-- Upfront fee -->
			<div v-if="isMinTermSelected" class="flex gap-2">
				<template v-if="isTerm12">
					<div
						v-if="
							planDiscount &&
							planDiscount?.rate !== 0 &&
							planDiscount?.key !== 'PLAN12M'
						"
						class="flex items-center gap-2"
					>
						<p class="line-through">
							{{ formatCurrency(upfrontCost) }}
						</p>
						<p>
							{{ formatCurrency(adjustedUpfrontCost) }} Upfront
							Fee
						</p>
						<CarbarBadge
							v-if="badgeText"
							line-height="leading-none"
							:background-color="badgeColor"
							:text-color="badgeTextColor"
							:class="isFlashSale ? 'order-first' : ''"
						>
							{{ discountText }}
						</CarbarBadge>
					</div>
					<div v-else class="flex items-center gap-2">
						<p>{{ formatCurrency(upfrontCost) }} Upfront Fee</p>
					</div>
				</template>
			</div>
			<div
				v-else-if="hasValidDiscountPlan"
				class="mb-2 flex flex-wrap items-center gap-2 md:mb-0"
			>
				<p class="line-through">
					{{ formatCurrency(upfrontCost) }}
				</p>
				<p>{{ discountedUpfrontCost }} Upfront Fee</p>
				<CarbarBadge
					v-if="badgeText"
					line-height="leading-none"
					:background-color="badgeColor"
					:text-color="badgeTextColor"
					:class="isFlashSale ? 'order-first' : ''"
				>
					{{ discountText }}
				</CarbarBadge>
			</div>
			<div v-else class="flex flex-wrap items-center gap-2">
				<p>
					{{
						upfrontCost
							? formatCurrency(upfrontCost)
							: formatCurrency(0)
					}}
					Upfront Fee
				</p>
			</div>

			<!-- Weeks -->
			<div class="mb-2 flex flex-wrap items-center gap-2 md:mb-0">
				<CarbarBadge
					v-if="isFlashSale"
					line-height="leading-none"
					:background-color="badgeColor"
				>
					{{ weeklyText }}
				</CarbarBadge>
				<p class="mt-1">Initial 4 weeks in advance</p>
			</div>

			<!-- Total min cost -->
			<p class="my-1">
				Total min. cost is
				{{ discountedTotalMinCost }}
				<sup>1</sup>
			</p>

			<div v-if="isListingOnly" class="flex items-center gap-2">
				<IconInfo />
				<span class="font-normal">
					Prices shown are before any discount
				</span>
			</div>

			<!-- Discount note -->
			<div
				v-if="isFlashSale"
				class="mb-2 flex flex-wrap items-center gap-2 md:mb-0"
			>
				<CarbarBadge
					v-if="weeklyText"
					line-height="leading-none"
					:background-color="badgeColor"
				>
					{{ discountSet?.note }}
				</CarbarBadge>
			</div>
		</div>
	</div>
</template>

<script setup>
import CarbarBadge from '@/components/badges/CarbarBadge.vue'
import IconInfo from '@/assets/svg/product-catalog/ico-moreinfo.svg'

const props = defineProps({
	marker: {
		type: Number,
		default: null,
		required: false,
	},
	pricing: {
		type: Number,
		required: true,
	},
	upfrontCost: {
		type: [Number, null],
		required: true,
	},
	totalMinCost: {
		type: Number,
		default: 0,
		required: false,
	},
	discountSet: {
		type: Object,
		required: false,
	},
	isMinTermSelected: {
		type: Boolean,
		default: false,
		required: false,
	},
	adjustedUpfrontCost: {
		type: Number,
		required: false,
	},
	adjustedTotalMinCost: {
		type: Number,
		default: 0,
		required: false,
	},
	hasValidDiscountPlan: {
		type: Boolean,
		default: false,
		required: false,
	},
	planDiscount: {
		type: Object,
		required: false,
	},
	term: {
		type: Number,
		required: false,
	},
})

const isListingOnly = useIsListingOnly()

const { badgeText, badgeColor, badgeTextColor } = getCampaignBadgeInfo(
	props.discountSet
)

const isFlashSale = computed(() => props.discountSet?.ref === 'flash_sale')
const discountText = computed(() =>
	isFlashSale.value ? props.discountSet?.rate + '% Off' : badgeText
)
const weeklyText = computed(() => props.discountSet?.weekly + 'weeks off')

const discountedUpfrontCost = computed(() => {
	const cost = props.discountSet?.upfront_fee ?? 0
	const rate = props.discountSet?.rate ?? 0

	// Divide rate by 100 before multiply.
	// Calculate remaining balance.
	const discountUpfront = Math.round(cost * (1 - rate / 100))

	return isFlashSale.value
		? formatCurrency(discountUpfront)
		: formatCurrency(props.discountSet?.upfront_fee)
})

const discountedTotalMinCost = computed(() => {
	const discountRate = props.discountSet?.rate / 100
	const flashSaleTotalMinCost = props.totalMinCost * discountRate

	return isFlashSale.value
		? formatCurrency(flashSaleTotalMinCost)
		: formatCurrency(props.totalMinCost)
})

const hasMarker = computed(
	() => props.marker !== null && props.marker !== undefined
)

const isTerm12 = computed(() => props.term === 12)
</script>
